import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { MediaItem, PresentedMediaItem } from '../../stores/reducers/media';
import UseSyncMedia from '../main-video/use-sync-media';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import { LOCATION_CANVAS_ROUTES } from '../../stores/reducers/video';

interface MediaAudioProps {
	id: string;
	mediaItem: MediaItem | undefined;
	mediaId: number;
	loop?: boolean;
	isPreloading: boolean;
	onLoadedDataOverride?: () => void;
	socket?: Socket | null;
	isEditingFeed: boolean;
	iterateLoadedMediaCount?: Function;
	presentedMediaItem?: PresentedMediaItem;
	specialEffects?: boolean;
}

const MediaAudioSpotlight = ((
	{
		id,
		mediaItem,
		loop,
		isPreloading,
		onLoadedDataOverride,
		mediaId,
		socket,
		isEditingFeed,
		iterateLoadedMediaCount,
		presentedMediaItem,
		specialEffects = true,
	}: MediaAudioProps,
) => {
	if (!mediaItem) {
		return (
			<></>
		);
	}

	const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
	const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);

	const isInitialOnsiteLocationCanvas = useMemo(
		() => !!onsiteLocationSession
			&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession.route)
			&& modalOpen,
		[onsiteLocationSession, modalOpen],
	);

	const src = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);
	const mediaElRef = useRef<HTMLAudioElement>(null);
	const echoCancelAudioRef = useRef<HTMLAudioElement>(null);
	const {

		onPlay,
		onEnded,
		onError,
		onLoadedData: onLoadedDataInitial,
		...otherSyncMediaProps
	} = UseSyncMedia({
		mediaElRef,
		echoCancelAudioRef,
		mediaId,
		mediaItem,
		socket,
		iterateLoadedMediaCount,
		isEditingFeed,
		presentedMediaItem,
		audioOnly: true,
		src,
	});

	const onLoadedData = useMemo(() => {
		if (isPreloading) {
			return () => {};
		} if (onLoadedDataOverride) {
			return onLoadedDataOverride;
		}
		return onLoadedDataInitial;
	}, [isPreloading, onLoadedDataInitial, onLoadedDataOverride]);

	const getAudioMimeType = (type: string) => {
		switch (type) {
			case 'aac':
				return 'audio/aac';
			case 'mp3':
				return 'audio/mpeg';
			case 'wav':
				return 'audio/wav';
			default:
				return '';
		}
	};

	const consolidatedListeners = isPreloading ? {} : {
		onEnded,
		onError,
		onPlay,
		...otherSyncMediaProps,
	};

	const ref = mediaElRef as any;
	useEffect(() => {
		if (ref?.current) {
			ref.current.offsetStart = specialEffects ? mediaItem.media_data.offsetStartSecond : 0;
		}
	}, [specialEffects, mediaItem.media_data.offsetStartSecond]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.offsetEnd = specialEffects ? mediaItem.media_data.offsetEndSecond : 0;
		}
	}, [specialEffects, mediaItem.media_data.offsetEndSecond]);
	useEffect(() => { 
		if (ref?.current) {
			ref.current.fadeIn = specialEffects ? [0, mediaItem.media_data.fadeIn] : [0, 0];
		}
	}, [specialEffects, mediaItem.media_data.fadeIn]);
	useEffect(() => {
		if (ref?.current) {
			ref.current.fadeOut = specialEffects ? [-1 * (mediaItem.media_data.fadeOut ?? 0), 0] : [0, 0];
		}
	}, [specialEffects, mediaItem.media_data.fadeOut]);
	useEffect(() => {
		if (mediaElRef?.current && echoCancelAudioRef?.current) {
			const ref = echoCancelAudioRef.current as any
			ref.mirror =  specialEffects ? mediaElRef.current : 0;
		}
	}, [specialEffects, mediaElRef.current, echoCancelAudioRef.current]);

	// Note: setSinkId is handled by the echo cancellation hook for these elements

	return (
		<>
			<audio
				id={id}
				ref={mediaElRef}
				is="advanced-audio"
				className="media-audio"
				onPlay={onPlay}
				onEnded={onEnded}
				loop={loop}
				controls={true}
				onLoadedData={onLoadedData}
				{...consolidatedListeners}
				crossOrigin={'anonymous'}
				muted={companionMode || isInitialOnsiteLocationCanvas}
			>
				<source
					src={src}
					type={getAudioMimeType(mediaItem?.type ?? '')}
				/>
			</audio>
			<audio
				is="advanced-audio"
				ref={echoCancelAudioRef}
			></audio>

		</>
	);
});

export default memo(MediaAudioSpotlight);
